import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, Play, X, Lock, Video } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getTeamProgram } from '../data/teamPrograms';
import { TeamLogo } from '../components/TeamLogo';

const ytId = (url = '') => (url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/) || [])[1];

const VideoModal = ({ video, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="team-video-modal">
    <div className="relative bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <button onClick={onClose} className="absolute top-2 right-2 z-10 w-9 h-9 rounded-full bg-white/90 text-forest shadow flex items-center justify-center" aria-label="Close" data-testid="team-video-close"><X size={18} /></button>
      <div className="aspect-video bg-black">
        <iframe
          title={video.title}
          src={`https://www.youtube-nocookie.com/embed/${ytId(video.videoUrl)}?rel=0`}
          className="w-full h-full"
          allow="accelerometer; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
      <div className="p-6">
        <h3 className="font-heading text-2xl text-forest mb-2">{video.title}</h3>
        {video.description && <p className="font-body text-stone-600 whitespace-pre-line leading-relaxed">{video.description}</p>}
      </div>
    </div>
  </div>
);

export default function TeamProgramPage({ programId }) {
  const params = useParams();
  const program = getTeamProgram(programId || params.programId);
  const { user, hasAccess, loading } = useAuth();
  const [active, setActive] = useState(null);

  if (!program) return <Navigate to="/programs" replace />;
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone-500 font-body">Loading…</div>;

  const back = (
    <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-8" data-testid="team-back">
      <ArrowLeft size={16} /> Back to Programs
    </Link>
  );

  if (!user || !hasAccess(program.id)) {
    return (
      <div className="min-h-screen bg-cream py-16 px-4" data-testid="team-access-gate">
        <div className="max-w-2xl mx-auto text-center">
          {back}
          <div className="bg-[#f5efe4] border border-stone-200 rounded-2xl p-10 md:p-14">
            <div className="flex justify-center mb-6"><TeamLogo program={program} /></div>
            <p className="inline-flex items-center gap-1.5 text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em] mb-3"><Lock size={12} /> Members Only</p>
            <h1 className="font-heading text-3xl md:text-4xl text-forest mb-4">{program.title}</h1>
            <p className="text-stone-600 font-body leading-relaxed">
              {!user ? 'Please log in from the Programs page to watch.' : "Your account doesn't have access yet — please reach out to Jill to be added."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-12 px-4" data-testid={`team-page-${program.id}`}>
      <div className="max-w-6xl mx-auto">
        {back}
        <div className="flex flex-col sm:flex-row sm:items-center gap-6 mb-10">
          <div className="bg-white rounded-2xl p-3 border border-stone-200 self-start"><TeamLogo program={program} /></div>
          <div>
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em] mb-2">Team Program</p>
            <h1 className="font-heading text-4xl md:text-5xl text-forest mb-2">{program.title}</h1>
            <p className="text-stone-600 font-body max-w-2xl">{program.tagline}</p>
          </div>
        </div>

        {program.videos.length === 0 ? (
          <div className="bg-white border border-dashed border-stone-300 rounded-2xl p-12 text-center" data-testid="team-empty">
            <Video size={28} className="mx-auto text-stone-300 mb-3" />
            <p className="font-heading text-xl text-forest mb-1">Videos coming soon</p>
            <p className="font-body text-sm text-stone-500">Jill is putting your program together — check back shortly.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {program.videos.map((v, i) => (
              <button key={v.title} onClick={() => setActive(v)} className="group text-left bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow" data-testid={`team-video-${program.id}-${i}`}>
                <div className="relative aspect-video bg-stone-100">
                  <img src={`https://img.youtube.com/vi/${ytId(v.videoUrl)}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" loading="lazy" />
                  <span className="absolute inset-0 flex items-center justify-center bg-forest/20 group-hover:bg-forest/30 transition-colors">
                    <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center"><Play size={18} className="text-forest ml-0.5" /></span>
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-lg text-forest">{v.title}</h3>
                  {v.description && <p className="font-body text-sm text-stone-500 line-clamp-2 mt-1">{v.description}</p>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
      {active && <VideoModal video={active} onClose={() => setActive(null)} />}
    </div>
  );
}
